import { IResourceComponentsProps, useUpdate } from "@pankod/refine-core";
import {
  List,
  Table,
  TextField,
  useTable,
  getDefaultSortOrder,
  DateField,
  Space,
  EditButton,
  DeleteButton,
  Button,
  Tag,
  Modal,
  Input,
  Form,
} from "@pankod/refine-antd";
import { IDevice } from "interfaces";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { useState } from "react";

export const DeviceList: React.FC<IResourceComponentsProps> = () => {
  const { tableProps, sorter } = useTable<IDevice>({
    initialSorter: [
      {
        field: "id",
        order: "desc",
      },
    ],
    initialPageSize: 15,
  });

  const { mutate: updateDevice } = useUpdate<IDevice>();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<IDevice | null>(null);
  const [form] = Form.useForm();

  const showAuthorizeModal = (device: IDevice) => {
    setSelectedDevice(device);
    form.setFieldsValue({
      nickname: device.nickname || ''
    });
    setIsModalVisible(true);
  };

  const handleAuthorize = () => {
    if (!selectedDevice) return;
    
    form.validateFields().then((values) => {
      updateDevice({
        resource: 'authorized-devices',
        id: selectedDevice.id,
        values: {
          status: 'authorized',
          nickname: values.nickname
        },
        successNotification: {
          message: 'Dispositivo autorizado correctamente',
          type: 'success'
        }
      });
      setIsModalVisible(false);
      setSelectedDevice(null);
      form.resetFields();
    });
  };

  const handleCancelModal = () => {
    setIsModalVisible(false);
    setSelectedDevice(null);
    form.resetFields();
  };

  const handleBlock = (id: number) => {
    updateDevice({
      resource: 'authorized-devices',
      id,
      values: {
        status: 'blocked'
      },
      successNotification: {
        message: 'Dispositivo bloqueado correctamente',
        type: 'success'
      }
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'authorized':
        return 'green';
      case 'blocked':
        return 'red';
      case 'pending':
        return 'orange';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'authorized':
        return 'Autorizado';
      case 'blocked':
        return 'Bloqueado';
      case 'pending':
        return 'Pendiente';
      default:
        return status;
    }
  };

  const parseDeviceInfo = (info: any) => {
    try {
      const parsed = typeof info === 'string' ? JSON.parse(info) : info;
      if (!parsed || typeof parsed !== 'object') return 'N/A';
      
      const parts: string[] = [];
      
      // Campos comunes que queremos mostrar en orden de prioridad
      const fields = [
        { key: 'model', label: 'Modelo' },
        { key: 'deviceName', label: 'Nombre' },
        { key: 'os', label: 'SO' },
        { key: 'osVersion', label: 'Versión SO' },
        { key: 'browser', label: 'Navegador' },
        { key: 'browserVersion', label: 'Versión' },
        { key: 'platform', label: 'Plataforma' },
        { key: 'deviceType', label: 'Tipo' },
      ];
      
      fields.forEach(({ key, label }) => {
        if (parsed[key]) {
          parts.push(`${label}: ${parsed[key]}`);
        }
      });
      
      // Si hay otras propiedades no incluidas, las agregamos también
      Object.keys(parsed).forEach(key => {
        if (!fields.some(f => f.key === key)) {
          parts.push(`${key}: ${parsed[key]}`);
        }
      });
      
      return parts.length > 0 ? parts.join(' | ') : 'N/A';
    } catch {
      return 'N/A';
    }
  };

  return (
    <List>
      <Table {...tableProps} rowKey="id">
        <Table.Column
          dataIndex="id"
          key="id"
          title="ID"
          render={(value) => <TextField value={value} />}
          defaultSortOrder={getDefaultSortOrder("id", sorter)}
          sorter
        />
        <Table.Column
          dataIndex="deviceIdentifier"
          key="deviceIdentifier"
          title="Identificador del Dispositivo"
          render={(value) => <TextField value={value} />}
          defaultSortOrder={getDefaultSortOrder("deviceIdentifier", sorter)}
          sorter
        />
        <Table.Column
          dataIndex="deviceInfo"
          key="deviceInfo"
          title="Información del Dispositivo"
          render={(value) => <TextField value={parseDeviceInfo(value)} />}
        />
        <Table.Column
          dataIndex="nickname"
          key="nickname"
          title="Nickname"
          render={(value) => <TextField value={value || 'Sin asignar'} />}
        />
        <Table.Column
          dataIndex="status"
          key="status"
          title="Estado"
          render={(value) => (
            <Tag color={getStatusColor(value)}>
              {getStatusLabel(value)}
            </Tag>
          )}
          defaultSortOrder={getDefaultSortOrder("status", sorter)}
          sorter
        />
        <Table.Column
          dataIndex="createdAt"
          key="createdAt"
          title="Fecha de Creación"
          render={(value) => <DateField value={value} format="DD/MM/YYYY HH:mm" />}
          defaultSortOrder={getDefaultSortOrder("createdAt", sorter)}
          sorter
        />
        <Table.Column<IDevice>
          title="Acciones"
          dataIndex="actions"
          render={(_, record) => (
            <Space>
              {record.status !== 'authorized' && (
                <Button
                  type="primary"
                  size="small"
                  icon={<CheckOutlined />}
                  onClick={() => showAuthorizeModal(record)}
                >
                  Autorizar
                </Button>
              )}
              {record.status !== 'blocked' && (
                <Button
                  danger
                  size="small"
                  icon={<CloseOutlined />}
                  onClick={() => handleBlock(record.id)}
                >
                  Bloquear
                </Button>
              )}
              <DeleteButton hideText size="small" recordItemId={record.id} />
            </Space>
          )}
        />
      </Table>
      
      <Modal
        title="Autorizar Dispositivo"
        visible={isModalVisible}
        onOk={handleAuthorize}
        onCancel={handleCancelModal}
        okText="Autorizar"
        cancelText="Cancelar"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="Nickname del Dispositivo"
            name="nickname"
            rules={[
              {
                required: true,
                message: 'Por favor ingrese un nickname para el dispositivo'
              }
            ]}
          >
            <Input 
              placeholder="Ej: iPhone de Juan, Tablet de la Dirección, etc."
              autoFocus
            />
          </Form.Item>
        </Form>
      </Modal>
    </List>
  );
};
